import React, { useEffect, useRef } from 'react';
import { Project, Station, CalculationResult } from '../../types';
import { MatrixType, MatrixData } from '../../lib/url/matrixMapping';
import { exportProject, generateGraphML, generateConfigJSON, cleanProjectName } from '../../lib/export/files';
import { downloadMatrix } from '../../lib/export/files';
import { generateExcelFile } from '../../lib/export/excel';
import { MATRIX_DEFINITIONS } from '../../lib/url/matrixMapping';

interface Props {
  project: Project;
  matrices: MatrixData;
  cy?: any;
}

export const ExportView: React.FC<Props> = ({ project, matrices, cy }) => {
  const cyRef = useRef(cy);

  useEffect(() => {
    if (cy) {
      cyRef.current = cy;
    }
  }, [cy]);

  const getTimestamp = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}_${hours}${minutes}`;
  };

  const downloadFile = (content: string | ArrayBuffer, filename: string, type: string = 'text/plain') => {
    const blob = new Blob([content], { type });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleSingleFileExport = async (type: MatrixType | 'config' | 'graphml' | 'excel') => {
    const timestamp = getTimestamp();
    const cleanedProjectName = cleanProjectName(project.name);
    console.log('ExportView: Starting export with cy:', cyRef.current);

    try {
      switch (type) {
        case 'excel':
          console.log('ExportView: Generating Excel file');
          const excelBuffer = await generateExcelFile({ project, matrices, cy: cyRef.current });
          console.log('ExportView: Excel file generated successfully', cyRef.current ? 'with network image' : 'without network image');
          downloadFile(excelBuffer, `${cleanedProjectName}_${timestamp}.xlsx`, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
          console.log('ExportView: Excel file downloaded');
          break;
        case 'graphml':
          if (!cyRef.current) {
            console.warn('ExportView: Cytoscape instance not available for GraphML export');
            return;
          }
          console.log('ExportView: Generating GraphML file');
          const graphmlContent = await generateGraphML(project);
          console.log('ExportView: GraphML file generated successfully');
          downloadFile(graphmlContent, `${cleanedProjectName}_${timestamp}.graphml`, 'application/xml');
          console.log('ExportView: GraphML file downloaded');
          break;
        case 'config':
          console.log('ExportView: Generating config JSON');
          const configContent = generateConfigJSON(project.config);
          console.log('ExportView: Config JSON generated successfully');
          downloadFile(configContent, `${cleanedProjectName}_${timestamp}.json`, 'application/json');
          console.log('ExportView: Config JSON downloaded');
          break;
        default:
          if (!cyRef.current) {
            console.warn('ExportView: Cytoscape instance not available for matrix export');
            return;
          }
          console.log(`ExportView: Generating ${type} matrix`);
          downloadMatrix(type, type === 'cities' ? matrices.cities : matrices[type], project);
          console.log(`ExportView: ${type} matrix downloaded`);
      }
    } catch (error) {
      console.error('Export failed:', error);
      alert('Failed to export project data. Please try again.');
    }
  };

  const handleExportAll = async () => {
    const timestamp = getTimestamp();
    const cleanedProjectName = cleanProjectName(project.name);
    console.log('ExportView: Starting full export with cy:', cyRef.current);

    try {
      // Dynamically build export matrices from MATRIX_DEFINITIONS
      const exportMatrices: CalculationResult = Object.keys(MATRIX_DEFINITIONS).reduce((acc, key) => ({
        ...acc,
        [key]: matrices[key as MatrixType] || []
      }), {} as CalculationResult);

      console.log('ExportView: Starting project export');
      const blob = await exportProject(project, exportMatrices, cyRef.current);
      console.log('ExportView: Project export completed successfully', cyRef.current ? 'with network image' : 'without network image');

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${cleanedProjectName}_${timestamp}.zip`;
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      console.log('ExportView: Download completed');
    } catch (error) {
      console.error('Export failed:', error);
      alert('Failed to export project. Please try again.');
    }
  };

  return (
    <div className="px-3 py-3 sm:p-8 pb-24 max-w-4xl mx-auto">
      <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
        <h2 className="text-xl font-medium text-[#4A4538] mb-4">Export Project Data</h2>
        <div className="mb-6">
          <table className="w-full">
            <thead>
              <tr className="border-b">
                <th className="text-left py-2">File</th>
                <th className="text-left py-2 hidden md:table-cell">Type</th>
                <th className="text-left py-2 hidden sm:table-cell">Format</th>
                <th className="text-right py-2">Action</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b bg-gray-50">
                <td className="py-2">Project Data</td>
                <td className="py-2 hidden md:table-cell">All Matrices</td>
                <td className="py-2 hidden sm:table-cell">Excel</td>
                <td className="text-right">
                  <button onClick={() => handleSingleFileExport('excel')} className="px-3 py-1 text-sm bg-[#6A8CAF] text-white rounded hover:bg-[#6A8CAF]/80">
                    Download
                  </button>
                </td>
              </tr>
              <tr className="border-b bg-gray-50">
                <td className="py-2">Network and Config</td>
                <td className="py-2 hidden md:table-cell">Layout Data</td>
                <td className="py-2 hidden sm:table-cell">GraphML</td>
                <td className="text-right">
                  <button onClick={() => handleSingleFileExport('graphml')} className="px-3 py-1 text-sm bg-[#6A8CAF] text-white rounded hover:bg-[#6A8CAF]/80">
                    Download
                  </button>
                </td>
              </tr>
              {Object.entries(MATRIX_DEFINITIONS)
                .filter(([_, definition]) => definition.allowInResults)
                .map(([key, definition]) => (
                <tr key={key} className="border-b">
                  <td className="py-2">{definition.displayName}</td>
                  <td className="py-2 hidden md:table-cell">Matrix</td>
                  <td className="py-2 hidden sm:table-cell">CSV</td>
                  <td className="text-right">
                    <button onClick={() => handleSingleFileExport(key as MatrixType)} className="px-3 py-1 text-sm bg-[#6A8CAF] text-white rounded hover:bg-[#6A8CAF]/80">
                      Download
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex justify-end space-x-4">
          <button
            onClick={handleExportAll}
            className="px-4 py-2 bg-[#6A8CAF] text-white rounded hover:bg-[#6A8CAF]/80"
          >
            Download All as ZIP
          </button>
        </div>
      </div>
    </div>
  );
}; 